import { useEffect, useState } from "react";
import { Phone, PhoneOff, Mic, MicOff } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { authedUrl } from "@/lib/api";

function useTicker(startedAt) {
  const [, force] = useState(0);
  useEffect(() => {
    if (!startedAt) return undefined;
    const id = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  if (!startedAt) return "00:00";
  const s = Math.max(0, Math.round((Date.now() - startedAt) / 1000));
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

function initials(name) {
  return (name || "").split(" ").map((s) => s[0]).slice(0, 2).join("");
}

/**
 * Fullscreen call overlay — used for outgoing, ringing (incoming), and in-call
 * phases. Voice-only for now, so no video element.
 */
export default function CallOverlay({
  phase,           // 'calling' | 'ringing' | 'in-call'
  peer,            // { id, full_name, avatar_url }
  muted,
  startedAt,
  remoteAudioRef,
  onAccept, onDecline, onEndCall, onCancelOutgoing, onToggleMute,
}) {
  const ticker = useTicker(startedAt);
  if (!phase || phase === "idle" || !peer) return null;

  const isIncoming = phase === "ringing";
  const isOutgoing = phase === "calling";
  const isLive = phase === "in-call";

  const statusText = isIncoming ? "Incoming voice call…"
                    : isOutgoing ? "Calling…"
                    : ticker;

  return (
    <div
      data-testid="call-overlay"
      className="fixed inset-0 z-[80] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white flex flex-col items-center justify-center"
    >
      <audio ref={remoteAudioRef} autoPlay />

      <div className="flex flex-col items-center gap-4">
        <div className="relative">
          {(isIncoming || isOutgoing) && (
            <span className="absolute inset-0 rounded-full animate-ping bg-white/20" />
          )}
          <Avatar className="h-32 w-32 ring-4 ring-white/20 relative">
            <AvatarImage src={authedUrl(peer.avatar_url)} />
            <AvatarFallback className="text-3xl bg-white/10 text-white">{initials(peer.full_name)}</AvatarFallback>
          </Avatar>
        </div>
        <div className="text-2xl font-semibold" data-testid="call-peer-name">{peer.full_name}</div>
        <div className="text-white/70 text-sm tabular-nums" data-testid="call-status">{statusText}</div>
      </div>

      <div className="mt-16 flex items-center gap-6">
        {isIncoming ? (
          <>
            <button
              data-testid="call-decline"
              onClick={onDecline}
              className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center shadow-lg transition"
              title="Decline"
            >
              <PhoneOff className="w-6 h-6" />
            </button>
            <button
              data-testid="call-accept"
              onClick={onAccept}
              className="w-16 h-16 rounded-full bg-emerald-500 hover:bg-emerald-600 flex items-center justify-center shadow-lg transition animate-pulse"
              title="Accept"
            >
              <Phone className="w-6 h-6" />
            </button>
          </>
        ) : (
          <>
            {isLive && (
              <button
                data-testid="call-mute"
                onClick={onToggleMute}
                className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition ${muted ? "bg-white text-slate-900" : "bg-white/20 hover:bg-white/30"}`}
                title={muted ? "Unmute" : "Mute"}
              >
                {muted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </button>
            )}
            <button
              data-testid="call-end"
              onClick={isOutgoing ? onCancelOutgoing : onEndCall}
              className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center shadow-lg transition"
              title={isOutgoing ? "Cancel" : "End call"}
            >
              <PhoneOff className="w-6 h-6" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
